import { useState, useCallback } from 'react'
import { Card, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { History, ChevronDown, ChevronRight, User as UserIcon } from 'lucide-react'
import { cn } from 'lib/utils'

export interface AuditEntry {
	_id: string
	action: string
	entityType: string
	entityId?: string
	details?: any
	user?: { _id: string; name?: string; email?: string }
	createdAt: string
}

interface AuditTrailProps {
	entries: AuditEntry[]
	total: number
	isLoading: boolean
	onLoadMore: () => void
	emptyText?: string
}

function formatTimestamp(iso: string): string {
	try {
		return new Date(iso).toLocaleString()
	} catch {
		return iso
	}
}

function actionColor(action: string): string {
	const a = action.toUpperCase()
	if (a.startsWith('CREATE') || a.startsWith('ADD')) return 'bg-green-100 text-green-700'
	if (a.startsWith('UPDATE') || a.startsWith('EDIT')) return 'bg-blue-100 text-blue-700'
	if (a.startsWith('DELETE') || a.startsWith('REMOVE')) return 'bg-red-100 text-red-700'
	if (a.startsWith('APPROVE') || a.includes('APPROVED')) return 'bg-emerald-100 text-emerald-700'
	if (a.startsWith('REJECT') || a.includes('REJECTED')) return 'bg-rose-100 text-rose-700'
	if (a.includes('SUBMIT')) return 'bg-amber-100 text-amber-700'
	return 'bg-gray-100 text-gray-700'
}

const AuditEntryRow = ({ entry }: { entry: AuditEntry }) => {
	const [expanded, setExpanded] = useState(false)
	const hasDetails = entry.details && Object.keys(entry.details).length > 0
	const userName = entry.user?.name || entry.user?.email || 'System'

	return (
		<div className="flex gap-3 px-3 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
			<div className="shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center mt-0.5">
				<UserIcon className="w-3.5 h-3.5 text-gray-500" />
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="font-medium text-sm text-gray-900">{userName}</span>
					<Badge className={cn('text-[10px]', actionColor(entry.action))}>{entry.action}</Badge>
					<Badge className="text-[10px] bg-gray-100 text-gray-600">{entry.entityType}</Badge>
					<span className="text-xs text-gray-400 ml-auto">{formatTimestamp(entry.createdAt)}</span>
				</div>
				{hasDetails && (
					<button
						type="button"
						onClick={() => setExpanded((v) => !v)}
						className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#0080fe]"
					>
						{expanded ? (
							<ChevronDown className="w-3 h-3" />
						) : (
							<ChevronRight className="w-3 h-3" />
						)}
						{expanded ? 'Hide details' : 'Show details'}
					</button>
				)}
				{hasDetails && expanded && (
					<pre className="mt-1 p-2 bg-gray-50 border border-gray-100 rounded text-[11px] text-gray-700 overflow-x-auto whitespace-pre-wrap break-words">
						{JSON.stringify(entry.details, null, 2)}
					</pre>
				)}
			</div>
		</div>
	)
}

const AuditTrail = ({
	entries,
	total,
	isLoading,
	onLoadMore,
	emptyText = 'No activity recorded yet.',
}: AuditTrailProps) => {
	const handleLoadMore = useCallback(() => {
		if (!isLoading) onLoadMore()
	}, [isLoading, onLoadMore])

	if (entries.length === 0 && !isLoading) {
		return (
			<Card>
				<CardContent className="py-10 text-center">
					<History className="mx-auto h-10 w-10 text-gray-300 mb-2" />
					<p className="text-sm text-gray-500">{emptyText}</p>
				</CardContent>
			</Card>
		)
	}

	const hasMore = entries.length < total

	return (
		<Card>
			<CardContent className="p-0">
				{entries.map((e) => (
					<AuditEntryRow key={e._id} entry={e} />
				))}
				{isLoading && entries.length === 0 && (
					<div className="text-center py-6 text-xs text-gray-400">Loading activity...</div>
				)}
				{hasMore && (
					<div className="text-center py-3 border-t border-gray-100">
						<Button variant="ghost" onClick={handleLoadMore} disabled={isLoading} className="text-xs">
							{isLoading ? 'Loading...' : `Load more (${total - entries.length} remaining)`}
						</Button>
					</div>
				)}
			</CardContent>
		</Card>
	)
}

export default AuditTrail
